import XanoAPI from "../lib/XanoAPI"; // Xano API instance

// Xano API instance
const instance = XanoAPI.getInstance();

/**
 * Fetches the TOTP secret and generates the otpauth URL for the logged-in user.
 * @returns {Promise<{ secret: string; otpauthUrl: string }>}
 */
async function fetchTOTPSecret(): Promise<{
  secret: string;
  otpauthUrl: string;
}> {
  try {
    const secret = await instance.registerTOTP();

    if (!secret) {
      throw new Error("Backend response missing TOTP secret.");
    }

    const issuer = "RPM"; // Replace with your app's name
    const userId = instance.getUserId().toString(); // Retrieve the user ID
    const accountName = `user:${userId}`; // Define the account label

    // Generate otpauth URL
    const otpauthUrl = `otpauth://totp/${encodeURIComponent(
      issuer
    )}:${encodeURIComponent(accountName)}?secret=${encodeURIComponent(
      secret
    )}&issuer=${encodeURIComponent(issuer)}&algorithm=SHA1&digits=6&period=30`;

    return { secret, otpauthUrl };
  } catch (error) {
    console.error(
      "Error fetching TOTP secret or generating otpauth URL:",
      error
    );
    throw new Error("Failed to fetch TOTP secret. Please try again.");
  }
}

/**
 * Verifies a TOTP token using the backend.
 * @param {string} totp - The TOTP token to verify.
 * @returns {Promise<{ valid: boolean }>} The verification result.
 */
async function verifyTOTPToken(
  totp: string
): Promise<{ valid: boolean; backup_codes?: any[] }> {
  try {
    const result = await instance.validateTOTP(totp);
    return {
      valid: result.isValid,
      backup_codes: result.backup_codes,
    };
  } catch (error) {
    console.error("Error verifying TOTP token:", error);
    throw new Error("Failed to verify TOTP token. Please try again.");
  }
}

/**
 * Enables 2FA by fetching the TOTP secret and otpauth URL.
 * @returns {Promise<{ secret: string; otpauthUrl: string }>}
 */
async function enable2FA(): Promise<{ secret: string; otpauthUrl: string }> {
  return await fetchTOTPSecret();
}

/**
 * Regenerates TOTP for a user (since TOTP cannot be disabled, only regenerated).
 * This will invalidate the previous TOTP and generate new backup codes.
 * @returns {Promise<{ secret: string; otpauthUrl: string }>}
 */
async function regenerateTOTP(): Promise<{
  secret: string;
  otpauthUrl: string;
}> {
  try {
    // Use the same logic as enable2FA since we're essentially re-enabling with a new secret
    const secret = await instance.registerTOTP();

    if (!secret) {
      throw new Error("Backend response missing TOTP secret.");
    }

    const issuer = "RPM"; // Replace with your app's name
    const userId = instance.getUserId().toString(); // Retrieve the user ID
    const accountName = `user:${userId}`; // Define the account label

    // Generate otpauth URL
    const otpauthUrl = `otpauth://totp/${encodeURIComponent(
      issuer
    )}:${encodeURIComponent(accountName)}?secret=${encodeURIComponent(
      secret
    )}&issuer=${encodeURIComponent(issuer)}&algorithm=SHA1&digits=6&period=30`;

    return { secret, otpauthUrl };
  } catch (error) {
    console.error("Error regenerating TOTP:", error);
    throw new Error("Failed to regenerate TOTP. Please try again.");
  }
}

// TODO: Check if this function is actually used - commented out for cleanup
// async function validateAndGetBackupCodes(
//   totp: string
// ): Promise<{ backupCodes: string[] }> {
//   return await instance.validateAndGetBackupCodes(totp);
// }

export {
  fetchTOTPSecret,
  verifyTOTPToken,
  enable2FA,
  regenerateTOTP,
  // TODO: Check if this export is actually used - commented out for cleanup
  // validateAndGetBackupCodes,
};
